import { RefObject, useRef } from "react"

import { useI18next } from "gatsby-plugin-react-i18next"
import gsap from "gsap"
import styled, { css } from "styled-components"

// import Callout from "components/TextElements/Callout"
import { ReactComponent as BlueArcSVG } from "images/homepage/blueArc.svg"
import { ReactComponent as GreenArcSVG } from "images/homepage/greenArc.svg"
import { ReactComponent as MagentaArcSVG } from "images/homepage/magentaArc.svg"
import colors from "styles/colors"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import useAnimation from "utils/useAnimation"
import useMedia from "utils/useMedia"
import links from "utils/links"
import { graphql, useStaticQuery } from "gatsby"
import media from "styles/media"
import Kicker from "components/TextElements/Kicker"
import { GatsbyImage } from "gatsby-plugin-image"
import Button from "components/buttons/Button"
import { checkedLocaleLink } from "utils/routes"


type Language = "en" | "es" | "pt"

const routes = {
  en: "/lp/javelin-2025-digital-issuance-galileo-leads/",
  es: "/es/lp/scorecard-2025-emision-digital-galileo-lider/",
  pt: "/pt/lp/scorecard-2025-emissao-digital-galileo-lidera/",
} as const

export default function SinglePoint({
  bgRef,
}: {
  bgRef: RefObject<HTMLDivElement> | null
}) {

  const startRef = useRef(null)
  const greenRef = useRef(null)
  const magentaRef = useRef(null)
  const blueRef = useRef(null)
  const { t, language } = useI18next()
  const route = routes[language as Language]
  const images: Queries.HomePageSinglePointQuery =
    useStaticQuery(graphql`
      query HomePageSinglePoint {
        cardStack: file(relativePath: { eq: "homepage/javelin.webp" }) {
          ...LocalImageQuery
        }
      }
    `)
  const javelin = images.cardStack?.childImageSharp?.gatsbyImageData

  useAnimation(() => {
    if (bgRef?.current)
      gsap.to(bgRef.current, {
        background: colors.lightMode,
        scrollTrigger: {
          trigger: startRef.current,
          scrub: true,
          start: "top center",
          end: "bottom center",
        },
      })

    gsap.to(greenRef.current, {
      rotate: 360,
      repeat: -1,
      ease: "linear",
      duration: 1.5,
    })

    gsap.to(magentaRef.current, {
      rotate: -360,
      repeat: -1,
      ease: "linear",
      duration: 1.8,
    })

    gsap.to(blueRef.current, {
      rotate: 360,
      repeat: -1,
      ease: "linear",
      duration: 4.5,
    })
  }, [bgRef])

  return (
    <OuterWrapper ref={startRef}>
      <Container>
        <Rings>
          <GreenArcSVG ref={greenRef} />
          <MagentaArcSVG ref={magentaRef} />
          <BlueArcSVG ref={blueRef} />
        </Rings>
        <BlueDot />
        <CalloutWrapper>
          <Callout>
            <Text>
              <ContentWrap>
                <Title>
                  {t("Galileo named Best-in-Class for digital issuance.")}
                </Title>
                {javelin && (
                  <JavelinImg
                    loading="lazy"
                    image={javelin}
                    alt={t("javelin Image")}
                  />
                )}
              </ContentWrap>
              <ContentWrap>
                <Body>
                  {t(
                    "Galileo earned the top spot in Javelin Strategy & Research’s 2025 Digital Issuance Provider Scorecard, recognized for its flexible, secure, and API-first platform."
                  )}
                </Body>
                <ButtonRow>
                  <Button
                    key={t("Learn More") ?? "Learn More"}
                    arrow={true}
                    styledLink={true}
                    lightMode={false}
                    href={checkedLocaleLink(route, language)}
                  >
                    {t("Learn More") ?? "Learn More"}
                  </Button>
                </ButtonRow>
              </ContentWrap>
            </Text>
          </Callout>
        </CalloutWrapper>
      </Container>
    </OuterWrapper>
  )
}

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  ${media.fullWidth} {
    margin-top: 40px;
    gap: 20px;
  }

  ${media.desktop} {
    margin-top: 2.41vw;
    gap: 1.2vw;
  }

  ${media.tablet} {
    margin-top: 3.906vw;
    gap: 1.95vw;
  }

  ${media.mobile} {
    gap: 5.333vw;
    margin-top: 3.906vw;
    width: 89.333vw;
  }
`

const JavelinImg = styled(GatsbyImage)`
  position: relative;

  ${media.fullWidth} {
    width: 190px;
    height: 190px;
    margin-bottom: 15px;
  }
  ${media.desktop} {
    width: 10vw;
    height: 10vw;
    margin-bottom: 1.5vw;
  }
  ${media.tablet} {
    width: 18vw;
    height: 18vw;
    margin-bottom: 2vw;
  }
  ${media.mobile} {
    width: 33vw;
    height: 33vw;
  }
`
const Title = styled.h3`
  ${text.h3}
  color:  ${colors.white};
  text-align: center;

  position: relative;
  z-index: 2;

  ${media.fullWidth} {
    margin-top: 20px;
    margin-bottom: 20px;
    margin-left: 0;
    width: 100%;
  }

  ${media.desktop} {
    margin-top: 1.2vw;
    margin-bottom: 1.2vw;
    margin-left: 0;
    width: 100%;
  }

  ${media.tablet} {
    margin-left: 0;
    width: 100%;
    margin-bottom: 1.953vw;
  }

  ${media.mobile} {
    ${text.h5}
    width: 100%;
    margin-bottom: 2.667vw;
  }
`

const Body = styled.div`
color: ${colors.blue10};
text-align:  center;
white-space: pre-wrap;
${text.bodyR};

  a {
    text-decoration: underline;
  }
  ${media.fullWidth} {
    width: 100%;
  }

  ${media.desktop} {
    width: 100%;
  }

  ${media.tablet} {
    width: 100%
    margin: unset;
  }

  ${media.mobile} {
    ${text.bodyR};
    width: 100%;
  }
`

const ContentWrap = styled.div<{ centered?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  ${media.mobile} {
    width: 100%;
  }
`

const Text = styled.div`
  position: relative;

  height: auto;
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;
  align-items: center;
  color: ${colors.blue300};
  ${media.fullWidth}{
  top:40px;
  }
  ${media.desktop}{
  top: 2vw;
  }
  ${media.tablet}{
  top: 5vw;
  }
  ${media.mobile}{
  top: 5vw;
  }
`

const Callout = styled.div`
  height: auto;
  display: flex;
  position: relative;
  z-index: 2;
  justify-content: center;
  margin-bottom: 50px;
  align-items: center;
`

const OuterWrapper = styled.section`
  width: 100%;
  background-color: transparent;
  display: flex;
  z-index: 1;
  justify-content: center;
  overflow: hidden;
  position: relative;

  ${fresponsive(css`
    padding: 200px 0 300px;
  `)}

  ${ftablet(css`
    padding: 200px 350px;
  `)}

  ${fmobile(css`
    padding: 200px 0 126px;
  `)}
`

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  ${fresponsive(css`
    width: 949px;
    height: 949px;
  `)}

  ${ftablet(css`
    width: 851px;
    height: 851px;
  `)}

  ${fmobile(css`
    width: 617px;
    height: 617px;
  `)}
`
const Rings = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;

  svg {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  ${ftablet(css`
    width: 949px;
    height: 949px;
  `)}

  ${fmobile(css`
    width: 617px;
    height: 617px;
  `)}
`
const BlueDot = styled.div`
  border-radius: 50%;
  background-color: ${colors.blue800};

  ${fresponsive(css`
    width: 851px;
    height: 851px;
  `)}

  ${ftablet(css`
    width: 851px;
    height: 851px;
  `)}

  ${fmobile(css`
    width: 617px;
    height: 617px;
  `)}
`

const CalloutWrapper = styled.div`
  a {
    position: relative;
    z-index: 1;
    pointer-events: auto;
  }
  position: absolute;
  ${fresponsive(css`
    width: 627px;
  `)}

  ${fmobile(css`
    width: 335px;
  `)}
`
